import Image from "next/image";
import { useState } from "react";
import { GiCheckMark } from "react-icons/gi";
import { PersistGate } from "redux-persist/integration/react";

import { cartContainsItem } from "../../../api/utils/helpers/cartContainsItem";
import { translationsContent } from "../../../localization/translations";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  addFormulaToCart,
  deleteFormulaFromCart,
  FormulaItem,
  removeAlcoholicBeverages,
  removeDessertFromFormula,
  removeFormulaFromCart,
} from "../../../redux/slices/cartSlice";
import { selectRestaurant } from "../../../redux/slices/restaurantSlice";
import { persistor } from "../../../redux/store";
import { useLanguage } from "../../../store/lang.context";
import imageLoader from "../../../utils/loader";
import { returnIndex } from "../../../utils/returnIndex";
import { translate } from "../../../utils/translate";
import CompositionModal from "../../menupage/composition";
import AddButton from "../../ui/button/AddButton";
import DarkButton from "../../ui/button/DarkButton";
import SubtractButton from "../../ui/button/SubtractButton";
import { myLoader } from "../../ui/cards/IngredientFormulaCard";
import classes from "./formulaItem.module.scss";

interface Props {
  formula: FormulaItem;
  id: number;
  isEatfid?: boolean;
  notSalad?: boolean;
}

const FormulaItemCard = ({
  formula,
  id,
  isEatfid = false,
  notSalad = false,
}: Props) => {
  const [modalVisible, setModalVisible] = useState(false);

  const dispatch = useAppDispatch();

  const restaurant = useAppSelector(selectRestaurant);

  const { language } = useLanguage();

  const handleOpenModal = () => {
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleOnClick = (add: boolean) => {
    if (add) {
      dispatch(addFormulaToCart({ formula: formula.formula, id: id }));
    } else {
      dispatch(removeFormulaFromCart({ formula: formula.formula, id: id }));
      dispatch(removeAlcoholicBeverages());
    }
  };
  const handleDeleteFromCart = () => {
    dispatch(deleteFormulaFromCart(id));
    dispatch(removeAlcoholicBeverages());
  };

  return (
    <PersistGate persistor={persistor} loading={<>Loading...</>}>
      <div className={classes.container}>
        <div className={classes.image}>
          <Image
            src={formula.formula.image}
            height="100%"
            width="100%"
            alt={formula.formula.title}
            loader={myLoader}
          />
        </div>
        <div className={classes.modify}>
          <span onClick={handleDeleteFromCart}>
            {translate(translationsContent, "supprimerButton", language)}
          </span>
          {!formula.recipeTitle && !formula.notModifiable && (
            <>
              <span>|</span>
              <span onClick={handleOpenModal}>
                {translate(translationsContent, "modifyButton", language)}
              </span>
            </>
          )}
        </div>
        <div className={classes.details}>
          {isEatfid && (
            <div className={classes.eatfidLogo}>
              <Image
                loader={imageLoader}
                width={120}
                height={60}
                src="fidelity.png"
                alt="La fidelité"
                objectFit="contain"
              />
            </div>
          )}
          <h3>{formula.formula[returnIndex(language)]}</h3>
          {formula.recipeTitle && <h4>{formula.recipeTitle}</h4>}
          <div className={classes.formulas}>
            {formula.bases.length > 0 && (
              <p>
                <div className={classes.checkmark}>
                  <GiCheckMark />
                </div>
                <p>
                  {notSalad
                    ? ""
                    : translate(
                        translationsContent,
                        "saladContent",
                        language
                      )}{" "}
                  {`${
                    formula.bases.length > 0
                      ? formula.bases.map(
                          (base) =>
                            `${base.ingredient[returnIndex(language)]}, `
                        )
                      : ""
                  }
                    ${
                      formula.extraBases.length > 0
                        ? formula.extraBases.map((base) => {
                            if (
                              cartContainsItem(
                                base.ingredient,
                                formula.bases
                              ) === -1
                            )
                              return `${
                                base.ingredient[returnIndex(language)]
                              }, `;
                          })
                        : ""
                    }
                    ${
                      formula.ingredients.length > 0
                        ? formula.ingredients.map(
                            (ingredient) =>
                              ` ${ingredient.ingredient[returnIndex(language)]}`
                          )
                        : ""
                    }
                    ${
                      formula.extraIngredients.length > 0
                        ? formula.extraIngredients.map((ingredient) => {
                            if (
                              cartContainsItem(
                                ingredient.ingredient,
                                formula.ingredients
                              ) === -1
                            )
                              return ` ${
                                ingredient.ingredient[returnIndex(language)]
                              }`;
                          })
                        : ""
                    }
                    ${
                      formula.sauces.length > 0
                        ? formula.sauces.map(
                            (sauce) =>
                              `, ${sauce.ingredient[returnIndex(language)]}`
                          )
                        : ""
                    }
                    ${
                      formula.extraSauces.length > 0
                        ? formula.extraSauces.map((sauce) => {
                            if (
                              cartContainsItem(
                                sauce.ingredient,
                                formula.sauces
                              ) === -1
                            )
                              return `, ${
                                sauce.ingredient[returnIndex(language)]
                              }`;
                          })
                        : ""
                    }
                  `}
                </p>
              </p>
            )}
            {formula.sides1.length > 0 && (
              <p className={classes.item}>
                <div className={classes.checkmark}>
                  <GiCheckMark />
                </div>
                <p>
                  {formula.sides1.map(
                    (side) => ` ${side.ingredient[returnIndex(language)]} `
                  )}
                  {`${
                    formula.extraSides1.length > 0
                      ? formula.extraSides1.map((side) => {
                          if (
                            cartContainsItem(
                              side.ingredient,
                              formula.sides1
                            ) === -1
                          )
                            return `, ${
                              side.ingredient[returnIndex(language)]
                            }`;
                        })
                      : ""
                  }`}
                </p>
              </p>
            )}
            {formula.sides2.length > 0 && (
              <p className={classes.item}>
                <div className={classes.checkmark}>
                  <GiCheckMark />
                </div>
                <p>
                  {formula.sides2.length > 0 &&
                    formula.sides2.map(
                      (side) => ` ${side.ingredient[returnIndex(language)]}`
                    )}
                  {`${
                    formula.extraSides2.length > 0
                      ? formula.extraSides2.map((side) => {
                          if (
                            cartContainsItem(
                              side.ingredient,
                              formula.sides2
                            ) === -1
                          )
                            return ` ${side.ingredient[returnIndex(language)]}`;
                        })
                      : ""
                  }`}
                </p>
              </p>
            )}

            {formula.desserts.length > 0 && (
              <>
                <p className={classes.item}>
                  <div className={classes.checkmark}>
                    <GiCheckMark />
                  </div>
                  <p>
                    {formula.desserts.map(
                      (dessert) =>
                        `${
                          dessert.dessert[returnIndex(language)]
                        }${dessert.toppings.map(
                          (topping) =>
                            `, ${topping.ingredient[returnIndex(language)]}`
                        )}${dessert.coulis.map(
                          (couli) =>
                            `, ${couli.ingredient[returnIndex(language)]}`
                        )}`
                    )}
                  </p>
                  <div className={`${classes.add} ${classes.inlineAdd}`}>
                    <DarkButton
                      label={translate(translationsContent, "remove", language)}
                      onClick={() => dispatch(removeDessertFromFormula({ id }))}
                    />
                  </div>
                </p>
              </>
            )}
          </div>
          <div className={classes.bottomContainer}>
            <p>
              <span>{formula.caloriesTotal}</span>{" "}
              {translate(translationsContent, "calories", language)}
            </p>
            <div className={classes.add}>
              <SubtractButton onClick={() => handleOnClick(false)} />
              <div className={classes.quantity}>{formula.quantity}</div>
              {!isEatfid && <AddButton onClick={() => handleOnClick(true)} />}
            </div>
            <div className={classes.price}>
              <p className={classes.header}>
                {translate(translationsContent, "ttc", language)}
              </p>
              <p>{(formula.formulaTotal * formula.quantity).toFixed(2)} €</p>
            </div>
          </div>
        </div>
      </div>
      {modalVisible && (
        <CompositionModal
          handleCloseModal={handleCloseModal}
          formula={formula.formula}
          categories={restaurant.mainCategories}
          sides={formula.formula.sides}
          id={id}
          modify={true}
          desserts={restaurant.dessertCategories}
        />
      )}
    </PersistGate>
  );
};

export default FormulaItemCard;
